import { useCallback } from 'react';
import { useI18n } from 'next-localization';

import { DISCLAIMER_MODAL_NAME } from '@/components/modals/disclaimer-modal/types';
import { useModalContext } from '@/components/modals/modal-context';
import { ProviderLocation, RedirectProfileState } from '@/features/profile/profile.types';
import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { CELEBRUS_BOOK_BUTTON_PREFIX, CELEBRUS_BOOK_BUTTON_PREFIX_EXTERNAL } from '@/lib/constants';
import { getBookingPageUrl, getPhysicianProfileUrl } from '@/lib/utils';
import { NeuButton } from '@hcahealthcaredpx/neutron-react';

type BookAppointmentSectionProps = {
  mhdParticipant: boolean;
  onlineSchedulingUrl?: string;
  location: ProviderLocation;
  urlTitle: string;
  phoneNumber: string;
  physicianName: string;
  redirectProfileState: RedirectProfileState;
  physicianIndex: number;
};

export const BookAppointmentSection = ({
  mhdParticipant,
  location,
  urlTitle,
  phoneNumber,
  physicianName,
  redirectProfileState,
  onlineSchedulingUrl,
  physicianIndex,
}: BookAppointmentSectionProps): JSX.Element => {
  const { routerPush } = useCustomRouting();
  const { withBasePath } = useBasePath();

  const { openModal } = useModalContext();

  const { setActiveDisplayLocation, setProviderLocations } = useDistanceContext();

  const { t } = useI18n();

  const viewProfileHandler = () => {
    setActiveDisplayLocation(redirectProfileState.activeLocation);
    setProviderLocations(redirectProfileState.providerLocations);
    const url = withBasePath(getPhysicianProfileUrl(urlTitle));
    routerPush(url);
  };

  const bookAppointmentHandler = () => {
    setActiveDisplayLocation(redirectProfileState.activeLocation);
    setProviderLocations(redirectProfileState.providerLocations);
    const url = withBasePath(getBookingPageUrl(urlTitle));
    routerPush(url);
  };

  const showBookingDisclaimerModal = useCallback(() => {
    if (!onlineSchedulingUrl) {
      return;
    }
    openModal(DISCLAIMER_MODAL_NAME.HEALOW_BOOKING, onlineSchedulingUrl);
  }, [openModal, onlineSchedulingUrl]);

  const showMhdAppointmentButtons =
    !onlineSchedulingUrl && mhdParticipant && location?.mhdCalendarId !== null;
  const showCarelinkAppointmentButtons = onlineSchedulingUrl;
  const showCallButtonsOnly = !showMhdAppointmentButtons && !showCarelinkAppointmentButtons;

  return (
    <div className="col-lg-7 d-flex">
      {showMhdAppointmentButtons && (
        <div className="align-items-center neu-width--100">
          <NeuButton
            className="w-100 mb-2"
            onClick={bookAppointmentHandler}
            id={`${CELEBRUS_BOOK_BUTTON_PREFIX}${physicianIndex}`}
          >
            {'Book an Appointment'}
            <span className="sr-only">{` ${t(
              'OAS.Listing.Accessibility.With'
            )} ${physicianName}`}</span>
          </NeuButton>
          <NeuButton className="w-100 mb-2" onClick={viewProfileHandler}>
            {`${t('OAS.Listing.View')} ${t('OAS.Listing.Profile')}`}
            <span className="sr-only">{physicianName}</span>
          </NeuButton>
          {location?.phone !== null && (
            <NeuButton
              className="w-100"
              kind="tertiary"
              icon="call"
              onClick={() => window.open(`tel:${location.phone}`, '_self')}
            >
              <span className="sr-only">{`${t(
                'OAS.Listing.Accessibility.Call'
              )} ${physicianName}`}</span>
              {location.phone}
            </NeuButton>
          )}
        </div>
      )}

      {showCallButtonsOnly && (
        <div className="w-100">
          {location.phone ? (
            <NeuButton
              className="w-100 mb-2"
              icon="call"
              onClick={() => window.open(`tel:${location.phone}`, '_self')}
            >
              <span className="sr-only">{`${t(
                'OAS.Listing.Accessibility.Call'
              )} ${physicianName}`}</span>
              {phoneNumber}
            </NeuButton>
          ) : null}
          <NeuButton className="w-100" onClick={viewProfileHandler}>
            {`${t('OAS.Listing.View')} ${t('OAS.Listing.Profile')}`}
            <span className="sr-only">{physicianName}</span>
          </NeuButton>
          <br />
        </div>
      )}

      {showCarelinkAppointmentButtons && (
        <div className="align-items-center neu-width--100">
          <NeuButton
            className="w-100 mb-2"
            onClick={showBookingDisclaimerModal}
            id={`${CELEBRUS_BOOK_BUTTON_PREFIX_EXTERNAL}${physicianIndex}`}
          >
            {t('OAS.Profile.Details.BookAppointment')}
            <span className="sr-only">{` ${t(
              'OAS.Listing.Accessibility.With'
            )} ${physicianName}`}</span>
          </NeuButton>
          <NeuButton className="w-100 mb-2" onClick={viewProfileHandler}>
            {`${t('OAS.Listing.View')} ${t('OAS.Listing.Profile')}`}
            <span className="sr-only">{physicianName}</span>
          </NeuButton>
          {location?.phone && (
            <NeuButton
              className="w-100"
              kind="tertiary"
              icon="call"
              onClick={() => window.open(`tel:${location.phone}`, '_self')}
            >
              <span className="sr-only">{`${t(
                'OAS.Listing.Accessibility.Call'
              )} ${physicianName}`}</span>
              {location.phone}
            </NeuButton>
          )}
        </div>
      )}
    </div>
  );
};
